import React, { useEffect, useState } from "react";
import {
  ShieldCheck, Download, CheckCircle2, AlertTriangle, RefreshCw,
  Loader2, Copy, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { proofApi } from "../lib/ledgerApi";

export default function ProofPanel({ txId }) {
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!txId) return;
    let alive = true;
    setLoading(true);
    proofApi
      .get(txId)
      .then((p) => { if (alive) { setProof(p); setErr(null); } })
      .catch((e) => {
        if (alive) setErr(e.response?.data?.detail?.code || e.response?.status || "ERR");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [txId]);

  const onVerify = async () => {
    if (!proof) return;
    setVerifying(true);
    try {
      const r = await proofApi.verify(proof);
      setVerification(r);
      toast.success(r.valid ? "Proof verified ✓" : "Proof INVALID", {
        description: r.details?.map((d) => `${d.check}=${d.ok ? "ok" : "FAIL"}`).join(" · "),
      });
    } catch (e) {
      toast.error("Verify failed");
    } finally {
      setVerifying(false);
    }
  };

  const onCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(proof, null, 2));
    toast.success("Proof JSON copied");
  };

  if (loading) {
    return (
      <div className="border border-border rounded-sm bg-card p-5" data-testid="proof-panel-loading">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 inline-flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Cryptographic proof
        </div>
        <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> building proof…
        </div>
      </div>
    );
  }

  if (err || !proof) {
    return (
      <div className="border border-border rounded-sm bg-card p-5" data-testid="proof-panel-unavailable">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 inline-flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Cryptographic proof
        </div>
        <div className="text-xs text-muted-foreground">
          Proof unavailable for this transaction (not yet confirmed on the
          chain). It will appear once the block is sealed.
        </div>
      </div>
    );
  }

  const sigs = proof.validator_signatures || [];
  const validCount = sigs.filter((s) => s.valid).length;

  return (
    <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="proof-panel">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground inline-flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Cryptographic proof
          {proof.legacy_single_sealer && (
            <span className="ml-1 px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-sm text-[9px] tracking-widest" data-testid="proof-legacy-badge">
              legacy single-sealer
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onVerify}
            disabled={verifying}
            data-testid="proof-verify-btn"
            className="text-[10px] uppercase tracking-widest border border-border rounded-sm px-2 py-1 hover:border-foreground inline-flex items-center gap-1"
          >
            {verifying
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} />}
            Verify online
          </button>
          <a
            href={proofApi.downloadUrl(txId)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="proof-download-btn"
            className="text-[10px] uppercase tracking-widest border border-border rounded-sm px-2 py-1 hover:border-foreground inline-flex items-center gap-1"
          >
            <Download className="w-3 h-3" strokeWidth={1.5} /> Download
          </a>
          <button
            onClick={onCopy}
            data-testid="proof-copy-btn"
            className="text-[10px] uppercase tracking-widest border border-border rounded-sm px-2 py-1 hover:border-foreground inline-flex items-center gap-1"
          >
            <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy JSON
          </button>
          <a
            href={proofApi.specMarkdownUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="proof-spec-link"
            className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <FileText className="w-3 h-3" strokeWidth={1.5} /> Spec
          </a>
        </div>
      </div>

      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] font-mono">
          <KV label="Block" value={`#${proof.block_number}`} />
          <KV label="Merkle steps" value={(proof.merkle_path || []).length} />
          <KV label="Validator sigs" value={`${validCount}/${sigs.length}`} />
          <KV label="Threshold" value={`${proof.consensus_valid_weight ?? "—"}/${proof.consensus_threshold ?? "—"}`} />
        </div>

        {!proof.legacy_single_sealer && sigs.length > 0 && (
          <div data-testid="proof-sigs-table" className="border border-border rounded-sm divide-y divide-border">
            {sigs.map((s, i) => (
              <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-[11px] items-center">
                <div className="col-span-3 font-mono">{s.validator_name}</div>
                <div className="col-span-6 font-mono text-[10px] text-muted-foreground truncate">
                  {s.sig?.slice(0, 32) || "—"}{s.sig?.length > 32 ? "…" : ""}
                </div>
                <div className="col-span-1 text-center">w{s.weight ?? 1}</div>
                <div className="col-span-2 text-right">
                  {s.byzantine_flag ? (
                    <span className="px-1 py-0.5 border border-status-tampered/40 bg-status-tampered/10 text-status-tampered rounded-sm text-[9px] uppercase tracking-widest">byzantine</span>
                  ) : s.valid ? (
                    <span className="px-1 py-0.5 border border-status-ok/40 bg-status-ok/10 text-status-ok rounded-sm text-[9px] uppercase tracking-widest">valid</span>
                  ) : (
                    <span className="px-1 py-0.5 border border-muted-foreground/30 bg-muted text-muted-foreground rounded-sm text-[9px] uppercase tracking-widest">missing</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {verification && (
          <div
            data-testid="proof-verify-result"
            className={`border rounded-sm p-3 text-xs ${
              verification.valid
                ? "border-status-ok/40 bg-status-ok/10 text-status-ok"
                : "border-status-tampered/40 bg-status-tampered/10 text-status-tampered"
            }`}
          >
            {verification.valid ? "✓ Proof verifies" : "⚠ Proof failed verification"}
            <ul className="mt-1 text-[10px] font-mono list-none space-y-0.5">
              {(verification.details || []).map((d, i) => (
                <li key={i}>
                  {d.ok ? "✓" : "✗"} {d.check}{d.note ? ` — ${d.note}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-[10px] text-muted-foreground font-mono">
          schema {proof.schema_version} · issued {new Date(proof.issued_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
      <div className="break-all">{value}</div>
    </div>
  );
}
